import { CustomerType } from 'store/customers/customer';
import { IndividualType } from 'store/individuals/individual';
import {
    EmailNotificationData,
    EmailNotificationOverallStatuses,
    EmailNotificationProviderStatuses,
} from 'types/common';
import { SortDirections } from 'utils/tableHelpers';

export type TaxLine = {
    quantity: number;
    description: string;
    unitPrice: number;
    netAmount: number;
    taxPercent: number;
    taxAmount: number;
};

export type AppliedAdditionalCharge = {
    id?: string;
    productCode: string;
    description: string;
    nominalCode: string;
    taxCode: string;
    amount: number;
    percentage?: number;
    quantity: number;
    active: boolean;
    customerId?: string | null;
};

export type InvoiceCustomer = {
    id: string;
    eori: string;
    vat: string;
    name: string;
    address: {
        addressLine1: string;
        addressLine2: string;
        postCode: string;
        city: string;
        country: string;
    };
    website: string;
    email: string;
    phone: string;
    deferredAccountNumber: string;
    specialInstructions: string;
    type: CustomerType;
    parentId: string;
    parentOfIds: string[];
    hasCertificates: true;
    referenceCode: string;
    traderAccountNumber: string;
    contact: {
        name: string;
        email: string;
        phoneNumber: string;
    };
    profilePictureLink: string;
};

export type Invoice = {
    id: string;
    declarationId: string;
    customer: InvoiceCustomer;
    recipient?: InvoiceCustomer | null;
    status: InvoicesStatuses;
    invoiceNumber?: number;
    orderNumber: string;
    jobId: string;
    manifestId: string | null;
    jobReferenceId: string;
    dueDate: string;
    mrn: string;
    taxLines: TaxLine[];
    additionalCharges: AppliedAdditionalCharge[];
    totalAmount: number;
    amountPaid: number | null;
    cancellationReason: string | null;
    archived: boolean;
    brokerId: string;
    updatedBy: string;
    updatedAt: string;
    issuer?: {
        id: string;
        fullName?: string;
        profileLink?: string;
    };
    dueDaysCount: number;
    createdBy: string;
    createdAt: string;
    sendToCustomerEmailStatus?: EmailNotificationData;
    billingRoute?: 'THIRD_PARTY_BILLING' | 'CUSTOMER_BILLING' | null;
};

export type UpdateInvoicePayload = Partial<{
    customer: {
        name: string;
        streetAndNumber: string;
        city: string;
        country: string;
        postCode: string;
    };
    dueDate: string;
    taxLines: TaxLine[];
    additionalCharges: AppliedAdditionalCharge[];
}>;

export type DocumentResponse = {
    declarationId: string;
    customerId: string;
    preSignedUrl: string;
    operationType: 'DOWNLOAD' | 'UPLOAD';
};

export enum InvoicesStatuses {
    DRAFT = 'DRAFT',
    // The purpose of this status is to indicate that we have sent the invoice to the customer,
    // but the email has not been received yet. When the invoice is successfully sent,
    // then it gets status AWAITING PAYMENT
    DRAFT_NON_EDITABLE = 'DRAFT_NON_EDITABLE',
    DISPUTED = 'DISPUTED',
    AWAITING_PAYMENT = 'AWAITING_PAYMENT',
    PAID = 'PAID',
    OVERDUE = 'OVERDUE',
    TO_BE_DUE = 'TO_BE_DUE',
    PARTIALLY_PAID = 'PARTIALLY_PAID',
    CANCELLED = 'CANCELLED',
}

export type ListInvoicesParams = Partial<{
    sortDirection: SortDirections;
    sort: ListInvoicesSortParams;
    page: number;
    size: number;
    customerId: string;
    listInvoiceStatus: InvoicesStatuses[];
    startDate: string;
    endDate: string;
    archived: boolean;
    query: string;
    customerName: string;
    createdBy: string;
    exactDate: string;
}>;

export type InvoiceStatusCountResponse = {
    status: InvoicesStatuses;
    count: number;
};

export enum ListInvoicesSortParams {
    STATUS = 'STATUS',
    ORDER_NUMBER = 'ORDER_NUMBER',
    DUE_DATE = 'DUE_DATE',
    JOB_REFERENCE_ID = 'JOB_REFERENCE_ID',
    MRN = 'MRN',
    TOTAL_AMOUNT = 'TOTAL_AMOUNT',
    UPDATED_AT = 'UPDATED_AT',
    CREATED_AT = 'CREATED_AT',
}

export type InvoiceHistoryRecord = {
    id: string;
    invoiceId: string;
    type: InvoiceHistoryActions;
    fieldName: string;
    oldValue: any;
    newValue: any;
    userRole: IndividualType;
    createdAt: string;
    creatorInfo: {
        id: string;
        fullName: string;
        profileLink: string;
    } | null;
    createdBy: string;
    email?: {
        recipient: string;
        status: EmailNotificationOverallStatuses;
        providerEventType: EmailNotificationProviderStatuses;
    };
};

export type GetInvoicesHistoryParams = Partial<{
    sortDirection: SortDirections;
    page: number;
    size: number;
    endDate: string;
    startDate: string;
}>;

export type GetInvoiceFilesResponse = {
    invoiceId: string;
    declarationId: string;
    invoiceStatus: InvoicesStatuses;
    mode: 'FIRST_SEND' | 'RESEND';
    defaultRecipient: string;
    recipientEditable: boolean;
    paidInvoiceResendConfirmationRequired: boolean;
    sendAllowed: boolean;
    sendToCustomerEmailStatus: EmailNotificationData;
    blockingErrors: string[];
    files: InvoiceFile[];
};

export enum InvoiceFileTypes {
    CUSTOMS_INVOICE = 'CUSTOMS_INVOICE',
    SAD = 'SAD',
    COMMERCIAL_INVOICE = 'COMMERCIAL_INVOICE',
}

export enum InvoiceFileState {
    PRESENT = 'PRESENT',
    MISSING = 'MISSING',
    SYSTEM_ERROR = 'SYSTEM_ERROR',
}

export type InvoiceFile = {
    type: InvoiceFileTypes;
    label: string;
    fileId: string;
    fileReference: string;
    fileName: string;
    state: InvoiceFileState;
    required: boolean;
    uploadAllowed: boolean;
    removeAllowedFromCurrentSend: boolean;
    previewAvailable: boolean;
    downloadUrl: string;
};

export type SendInvoiceToCustomerPayload = {
    recipient: string;
    files: {
        type: InvoiceFileTypes;
        fileReference: string;
    }[];
    idempotencyKey: string; // UUID
};

export type SendInvoiceToCustomerResponse = {
    invoiceId: string;
    invoiceStatus: InvoicesStatuses;
    sendToCustomerEmailStatus: EmailNotificationData;
    dispatchId: string;
    recipient: string;
    message: string;
};

export type UpdateInvoiceStatusPayload = {
    status:
        | InvoicesStatuses.CANCELLED
        | InvoicesStatuses.PAID
        | InvoicesStatuses.PARTIALLY_PAID
        | InvoicesStatuses.DISPUTED;
    amountPaid?: string;
    cancellationReason?: string;
    disputeReason?: string;
};

export type InvoiceHistoryActions =
    | 'PARTIALLY_PAID'
    | 'CANCELLED'
    | 'DISPUTED'
    | 'CREATED'
    | 'UPDATED'
    | 'SENT'
    | 'RESENT'
    | 'PAID'
    | 'ARCHIVED'
    | 'UNARCHIVED'
    | 'EXPORTED_TO_CSV'
    | 'EXPORTED_TO_PDF'
    | 'REISSUED';
